import React, { useCallback, useMemo, useState } from 'react';
import { getSummaryMetrics, getCategoryLookups } from 'js/utils/question-set';

import { Button } from 'reactstrap';
import { AssessmentSelector } from './AssessmentSelector';
import { AssessmentDifferentialChart } from './AssessmentDifferentialChart';
import { QuestionSetDifferentialChart } from './QuestionSetDifferentialChart';
import { DEFAULT_STALE_DATA_MAX_AGE_MS } from './utils';

import NoAssessmentSelected from './NoAssessmentSelected';

import styles from './AssessmentDifferential.module.scss';

const DETAIL_LEVEL = {
  ALL_QUESTIONS: 'all-questions',
  ROLLUP: 'rollup',
};

export const AssessmentDifferential = ({
  assessments,
  footerComponent: FooterComponent,
  showStale = false,
  staleAge = DEFAULT_STALE_DATA_MAX_AGE_MS,
}) => {
  // TODO: intialize this to something reasonable (first/last, last two, last one and closest to 1 year ago?)
  const categoryLookups = useMemo(() => {
    const summaryMetrics = getSummaryMetrics(assessments);
    return getCategoryLookups(assessments, summaryMetrics.categories);
  }, [assessments]);

  const [summaryData, setSummaryData] = useState({
    assessments: null,
    summaryMetrics: null,
  });

  const [detailLevel, setDetailLevel] = useState(DETAIL_LEVEL.ALL_QUESTIONS);

  const toggleDetailLevel = useCallback(() => {
    setDetailLevel(
      DETAIL_LEVEL.ROLLUP === detailLevel
        ? DETAIL_LEVEL.ALL_QUESTIONS
        : DETAIL_LEVEL.ROLLUP
    );
  }, [detailLevel]);

  const handleSelectAssessments = (assessmentsToCompare) => {
    setSummaryData({
      assessments: assessmentsToCompare,
      summaryMetrics: getSummaryMetrics(
        assessmentsToCompare.filter((x) => x !== null)
      ),
    });
  };

  const [before, after] =
    summaryData.assessments === null
      ? [null, null]
      : [
          summaryData.summaryMetrics?.assessments[
            summaryData.assessments[0]?.id
          ],
          summaryData.summaryMetrics?.assessments[
            summaryData.assessments[1]?.id
          ],
        ];

  const noAssessmentsSelected = !before && !after;
  const questionSetCount = useMemo(
    () => Object.keys(summaryData?.summaryMetrics?.questionSets ?? {}).length,
    [summaryData]
  );

  const DifferentialChart = useMemo(() => {
    if (questionSetCount <= 1 || detailLevel === DETAIL_LEVEL.ALL_QUESTIONS) {
      return AssessmentDifferentialChart;
    }
    return QuestionSetDifferentialChart;
  }, [questionSetCount, detailLevel]);

  const renderHeaderActions = useCallback(() => {
    return questionSetCount > 1 ? (
      <div className={styles['detail-level']}>
        <Button outline color="info" size="sm" onClick={toggleDetailLevel}>
          {detailLevel === DETAIL_LEVEL.ALL_QUESTIONS
            ? 'Full detail'
            : 'Rollup'}
        </Button>
      </div>
    ) : null;
  }, [questionSetCount, detailLevel, toggleDetailLevel]);

  return (
    <section className={styles['assessment-differential']}>
      <div className={styles.selector}>
        <AssessmentSelector
          assessments={assessments}
          onSelectAssessments={handleSelectAssessments}
        />
      </div>
      <div className={styles.chart}>
        {noAssessmentsSelected ? (
          <NoAssessmentSelected />
        ) : (
          <>
            <DifferentialChart
              assessments={[before, after]}
              staleAge={staleAge}
              showStale={showStale}
              summaryMetrics={summaryData.summaryMetrics}
              categoryLookups={categoryLookups}
              renderHeaderActions={renderHeaderActions}
            />
            {FooterComponent && <FooterComponent />}
          </>
        )}
      </div>
    </section>
  );
};

export default AssessmentDifferential;
